/* eslint-disable complexity */
import { type JSX, useRef } from 'react';

import { Box } from '@change-corgi/design-system-legacy/layout';

import {
	usePetitionDetailsPageData,
	usePetitionDetailsPageUserData,
} from 'src/app/pages/petitionGamma/details/pageContext';
import { ResponsiveBox, SectionSeparator, Separator } from 'src/app/pages/petitionGamma/details/shared/components';
import {
	AfterDescriptionWaypoint,
	AfterMobileActionButtonWaypoint,
} from 'src/app/pages/petitionGamma/details/shared/waypoints';
import { MembershipAwareness, MembershipAwarenessSunrise } from 'src/app/shared/components/membershipAwareness';

import { PetitionStarter } from '../components/PetitionStarter';
import { PetitionStarter as PetitionStarterSunrise } from '../components/PetitionStarterSunrise';
import { useIsFullSunriseEnabled } from '../hooks/isFullSunriseEnabled';
import { useStickyBehavior } from '../hooks/mainContainer/useStickyBehavior';

import { ContentSummary } from './contentSummary';
import { ContentSummary as ContentSummarySunrise } from './contentSummarySunrise';
import { DecisionMakerSection } from './DecisionMakerSection';
import { DescriptionContainer } from './description';
import { DescriptionSunriseContainer } from './descriptionSunrise';
import { HeadingContainerMobile } from './HeadingMobile';
import { MediaHitsContainer } from './MediaHitsContainer';
import { MediaHitsContainerSunrise } from './MediaHitsContainerSunrise';
import { MobileActionButtonContainer } from './mobileActionButton';
import { MobileActionButtonContainer as MobileActionButtonContainerSunrise } from './mobileActionButtonSunrise';
import { PetitionTimestamp } from './PetitionTimestamp';
import { RecentSignersViewportNormal, RecentSignersViewportSmall } from './recentSigners';
import {
	RecentSignersViewportNormal as RecentSignersViewportNormalSunrise,
	RecentSignersViewportSmall as RecentSignersViewportSmallSunrise,
} from './recentSignersSunrise';
import { ReportAbuseContainer } from './ReportAbuse';
import { ReportAbuseContainerSunrise as ReportAbuseSunriseContainer } from './ReportAbuseSunrise';
import { SapCtaContainer } from './SapCta';
import { ScrollTrackingWaypointContainer } from './ScrollTrackingWaypoint';
import { SharePetition } from './share/SharePetition';
import { SharePetition as SharePetitonSunrise } from './shareSunriseExperiment/SharePetition';
import { ShareVisualContainer } from './ShareVisualContainer';
import { SideContainer } from './side';
import { SideContainer as SideContainerSunrise } from './sideSunrise';
import { SignatureStatsMobileContainer } from './stats/signatureStats';
import { TagBreadcrumbs } from './TagBreadcrumbs';
import { TheSupporters } from './TheSupporters';
import { TheSupportersSunrise } from './TheSupportersSunrise';
import { UpdatesContainer } from './Updates';

/*
Using CSS grid for better keyboard/swipe navigation
╔═══╦═══╗
║ 1 ║   ║
╠═══╣ 2 ║
║ 3 ║   ║
╚═══╩═══╝
*/

// eslint-disable-next-line max-lines-per-function
export function MainContainer(): JSX.Element {
	const {
		data: {
			petition: { id: petitionId, slug },
		},
	} = usePetitionDetailsPageData();
	const {
		data: { signed },
	} = usePetitionDetailsPageUserData();

	const signatureBlockRef = useRef<HTMLDivElement>(null);
	const stickyStyles = useStickyBehavior(signatureBlockRef);

	const {
		data: {
			isSunriseDescriptionEnabled,
			isSunriseSupportersEnabled,
			isSunriseRecentSigners,
			isSunriseShareModuleExperimentEnabled,
			isSunriseActionPanelEnabled,
			isSunriseContentSummaryEnabled,
			isSunrisePetitionStarterEnabled,
			isSunriseMediaHitsEnabled,
			isSunriseReportAbuseEnabled,
			isSunriseMembershipAwarenessEnabled,
		},
	} = useIsFullSunriseEnabled();

	return (
		<Box
			mb={64}
			sx={{
				gridTemplateColumns: '66.66667% 33.33333%',
				'&': {
					// necessary to override ms-grid above
					display: ['block', 'grid'],
				},
			}}
		>
			<Box sx={{ gridColumn: '1 / 1' }}>
				<HeadingContainerMobile />

				<Box px={[16, 0]}>
					<ResponsiveBox viewportSize="normal">
						{isSunriseRecentSigners ? <RecentSignersViewportNormalSunrise /> : <RecentSignersViewportNormal />}

						<ShareVisualContainer />

						{isSunriseContentSummaryEnabled ? <ContentSummarySunrise /> : <ContentSummary />}
						{/* Replace the ifTrue expression with the Sunrise version */}
						{isSunriseDescriptionEnabled ? <DescriptionSunriseContainer /> : <DescriptionContainer />}
						{isSunriseReportAbuseEnabled ? <ReportAbuseSunriseContainer /> : <ReportAbuseContainer />}
						{isSunrisePetitionStarterEnabled ? <PetitionStarterSunrise /> : <PetitionStarter />}
					</ResponsiveBox>

					<ResponsiveBox viewportSize="small">
						{isSunriseActionPanelEnabled ? (
							<MobileActionButtonContainerSunrise sticky={false} />
						) : (
							<MobileActionButtonContainer sticky={false} />
						)}
						<AfterMobileActionButtonWaypoint />
						<SignatureStatsMobileContainer />
						{isSunriseRecentSigners ? <RecentSignersViewportSmallSunrise /> : <RecentSignersViewportSmall />}

						<ShareVisualContainer />

						{isSunriseContentSummaryEnabled ? <ContentSummarySunrise /> : <ContentSummary />}
						{/* Replace the ifTrue expression with the Sunrise version */}
						{isSunriseDescriptionEnabled ? <DescriptionSunriseContainer /> : <DescriptionContainer />}
						{isSunriseReportAbuseEnabled ? <ReportAbuseSunriseContainer /> : <ReportAbuseContainer />}
						{isSunrisePetitionStarterEnabled ? <PetitionStarterSunrise /> : <PetitionStarter />}
					</ResponsiveBox>

					<ScrollTrackingWaypointContainer name="scroll_past_body" />
					<AfterDescriptionWaypoint />
				</Box>
			</Box>

			<Box sx={{ gridColumn: '2 / 2', gridRow: '1 / span 2' }} pl={[0, 40]}>
				<ResponsiveBox viewportSize="small">
					{isSunriseActionPanelEnabled ? (
						<MobileActionButtonContainerSunrise sticky />
					) : (
						<MobileActionButtonContainer sticky />
					)}
				</ResponsiveBox>
				<ResponsiveBox viewportSize="normal" sx={{ ...stickyStyles }}>
					{isSunriseActionPanelEnabled ? (
						<SideContainerSunrise containerRef={signatureBlockRef} />
					) : (
						<SideContainer containerRef={signatureBlockRef} />
					)}
				</ResponsiveBox>
			</Box>

			<Box sx={{ gridColumn: '1 / 1', gridRow: '2 / 2' }} px={[16, 0]}>
				<SapCtaContainer />

				<DecisionMakerSection />

				{isSunriseSupportersEnabled ? <TheSupportersSunrise /> : <TheSupporters />}

				{!isSunriseMembershipAwarenessEnabled && (
					<Box my={24}>
						<MembershipAwareness
							sourceLocation="petition_details"
							petitionId={petitionId}
							slug={slug}
							signed={signed}
						/>
					</Box>
				)}
			</Box>

			{isSunriseMembershipAwarenessEnabled && (
				<Box
					sx={{
						gridColumn: '1 / -1',
					}}
					my={24}
					px={[16, 0]}
				>
					<MembershipAwarenessSunrise
						sourceLocation="petition_details"
						petitionId={petitionId}
						slug={slug}
						signed={signed}
					/>
				</Box>
			)}

			<Box sx={{ gridColumn: '1' }}>
				<ScrollTrackingWaypointContainer name="scroll_updates_section" />

				<SectionSeparator />
				<UpdatesContainer />

				{!isSunriseMediaHitsEnabled && <MediaHitsContainer />}
			</Box>
			{isSunriseMediaHitsEnabled && (
				<Box
					sx={{
						gridColumn: '1 / -1',
					}}
				>
					<MediaHitsContainerSunrise />
				</Box>
			)}
			<Box sx={{ gridColumn: '1 / 1', gridRow: '4 / 4' }} px={[16, 0]}>
				<SectionSeparator />
				{isSunriseShareModuleExperimentEnabled ? <SharePetitonSunrise /> : <SharePetition />}

				<SectionSeparator />
				<PetitionTimestamp />
				<Separator />
				<TagBreadcrumbs />
			</Box>
		</Box>
	);
}
